import { useEffect } from 'react'
import type { MessageKey, MessageVars } from '@/i18n'
import type { PassportView } from './model'

function setMeta(attr: 'name' | 'property', key: string, content: string): () => void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  const created = !el
  const before = el?.content ?? null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.content = content
  return () => {
    if (created) el.remove()
    else if (before !== null) el.content = before
  }
}

/**
 * Title, description and share card for the Passport (a pouch link is often shared in chats).
 * Only a released lot's Passport may be indexed; warning and not-found pages never are.
 */
export function usePageMeta(view: PassportView | null, t: (k: MessageKey, v?: MessageVars) => string): void {
  const title =
    view?.kind === 'PASSPORT'
      ? t('passport.meta.title', { product: view.passport.product.name, district: view.passport.origin.district ?? '' })
      : view
        ? t('passport.meta.warnTitle', { code: view.code })
        : t('passport.meta.loading')
  const description =
    view?.kind === 'PASSPORT'
      ? t('passport.meta.description', {
          crop: view.passport.origin.crop,
          district: view.passport.origin.district ?? '',
          code: view.code,
        })
      : t('passport.meta.warnDescription')
  const indexable = view?.kind === 'PASSPORT'

  useEffect(() => {
    const prevTitle = document.title
    document.title = title
    const undo = [
      setMeta('name', 'description', description),
      setMeta('property', 'og:title', title),
      setMeta('property', 'og:description', description),
      setMeta('property', 'og:type', 'website'),
      setMeta('name', 'robots', indexable ? 'index, follow' : 'noindex, nofollow'),
    ]
    return () => {
      document.title = prevTitle
      undo.forEach((u) => u())
    }
  }, [title, description, indexable])
}
